import React, { useState, useEffect, useRef } from "react";
import CloudUsersFormApi from "lib/api/CloudUsersForm";
import logo from "components/common/AppHeader/logo.svg";

function emptyRow() {
  return { first_name: "", last_name: "", email: "", user_type: "standard" };
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
}

// Minimal CSV parser handling quoted fields and commas within quotes.
function parseCSV(text) {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
}

function normalizeRole(value) {
  const v = (value || "").trim().toLowerCase();
  if (v === "admin") return "admin";
  return "standard";
}

const pageWrap = {
  minHeight: "100vh",
  background: "#e9ecef",
  padding: "20px 16px",
  fontFamily: "'Source Sans Pro', Helvetica, Arial, sans-serif",
  color: "#111827",
};
const cardWrap = {
  maxWidth: 880,
  margin: "0 auto",
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 8,
  padding: "20px 24px 28px",
};
const th = {
  textAlign: "left",
  padding: "10px 10px",
  fontSize: 11,
  fontWeight: 600,
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  border: "none",
  borderBottom: "2px solid #e5e7eb",
  whiteSpace: "nowrap",
  background: "#f3f4f6",
  height: "auto",
  lineHeight: "normal",
};
const td = {
  padding: "6px 8px",
  border: "none",
  borderBottom: "1px solid #f3f4f6",
  verticalAlign: "middle",
  lineHeight: "normal",
};
const input = {
  width: "100%",
  boxSizing: "border-box",
  padding: "8px 10px",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  fontSize: 14,
};
const primaryBtn = {
  background: "#1a82e2",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  padding: "12px 24px",
  fontSize: 15,
  fontWeight: 600,
  cursor: "pointer",
};
const iconBtn = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "#fff",
  color: "#6b7280",
  border: "1px solid #d1d5db",
  borderRadius: 6,
  padding: "6px 8px",
  cursor: "pointer",
  lineHeight: 0,
};

export default function CloudUsersForm(props) {
  const { client_uuid, token } = props.match.params;

  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState(null);
  const [invalidReason, setInvalidReason] = useState(null);
  const [rows, setRows] = useState([]);
  const [fixedSeats, setFixedSeats] = useState(false);
  const [importError, setImportError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    async function loadContext() {
      try {
        const response = await CloudUsersFormApi.load(client_uuid, token);
        if (!response || response.valid === false) {
          setInvalidReason(
            (response && response.reason) ||
              "This link is no longer valid. It may have expired or already been used."
          );
        } else {
          setContext(response);
          const seats = response.seat_count;
          if (Number.isInteger(seats) && seats > 0) {
            setFixedSeats(true);
            setRows(Array.from({ length: seats }, () => emptyRow()));
          } else {
            setFixedSeats(false);
            setRows([emptyRow()]);
          }
        }
      } catch (e) {
        setInvalidReason("We couldn't load this form. Please try again later.");
      }
      setLoading(false);
    }
    loadContext();
  }, [client_uuid, token]);

  function updateRow(index, field, value) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  }

  function addRow() {
    setRows((prev) => [...prev, emptyRow()]);
  }

  function removeRow(index) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  function downloadTemplate() {
    const csv = "First Name,Last Name,Email,Role\nJane,Doe,jane.doe@example.com,Admin\n";
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "cloud-users-template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function handleImport(e) {
    setImportError("");
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = parseCSV(ev.target.result);
        if (parsed.length === 0) {
          setImportError("The file appears to be empty.");
          return;
        }
        // Drop a header row if present.
        let dataRows = parsed;
        const first = parsed[0].map((c) => (c || "").trim().toLowerCase());
        if (first.includes("first name") || first.includes("email")) {
          dataRows = parsed.slice(1);
        }
        const imported = dataRows.map((cols) => ({
          first_name: (cols[0] || "").trim(),
          last_name: (cols[1] || "").trim(),
          email: (cols[2] || "").trim(),
          user_type: normalizeRole(cols[3]),
        }));
        if (fixedSeats) {
          const seats = context.seat_count;
          if (imported.length > seats) {
            setImportError(
              `The file has ${imported.length} rows but only ${seats} seat(s) are licensed. Please remove extra rows.`
            );
            return;
          }
          const next = Array.from({ length: seats }, (_, i) => imported[i] || emptyRow());
          setRows(next);
        } else {
          setRows(imported.length ? imported : [emptyRow()]);
        }
      } catch (err) {
        setImportError("We couldn't read that file. Please use the provided template.");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  const hasAdmin = rows.some((r) => r.user_type === "admin");

  const allRowsValid =
    rows.length > 0 &&
    hasAdmin &&
    rows.every(
      (r) =>
        r.first_name.trim() &&
        r.last_name.trim() &&
        isValidEmail(r.email) &&
        (r.user_type === "admin" || r.user_type === "standard")
    );

  async function submit() {
    setSubmitting(true);
    setSubmitResult(null);
    try {
      const payload = {
        users: rows.map((r) => ({
          first_name: r.first_name.trim(),
          last_name: r.last_name.trim(),
          email: r.email.trim(),
          user_type: r.user_type,
        })),
      };
      const response = await CloudUsersFormApi.submit(client_uuid, token, payload);
      if (response.status === 200 || response.status === 201) {
        setSubmitResult({ ok: true, message: response.message || "Thank you — your cloud users have been submitted." });
      } else {
        setSubmitResult({ ok: false, message: response.message || "Submission failed. Please try again." });
      }
    } catch (e) {
      setSubmitResult({
        ok: false,
        message:
          "An error occurred. Please try again, or contact ComputerEaseAdminDL@deltek.com if the problem continues.",
      });
    }
    setSubmitting(false);
  }

  if (loading) {
    return (
      <div style={pageWrap}>
        <div style={cardWrap}>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (invalidReason) {
    return (
      <div style={pageWrap}>
        <div style={cardWrap}>
          <h1 style={{ fontSize: 22, marginTop: 0 }}>Link no longer valid</h1>
          <p style={{ color: "#4b5563" }}>{invalidReason}</p>
          <p style={{ color: "#4b5563" }}>
            If you already completed this form, it has been locked and no
            further changes can be made online. Please contact ComputerEase
            Support if you need to make changes, or to request a new link.
          </p>
        </div>
      </div>
    );
  }

  if (submitResult && submitResult.ok) {
    return (
      <div style={pageWrap}>
        <div style={cardWrap}>
          <h1 style={{ fontSize: 22, marginTop: 0 }}>Thank you!</h1>
          <p style={{ color: "#15803d" }}>
            Your cloud users have been submitted and will receive their cloud credentials via
            email within 2 business days.
          </p>
          <p style={{ color: "#4b5563" }}>
            This form has now been completed and is locked. If you need to make changes, please
            contact <a href="mailto:ComputerEaseAdminDL@deltek.com">ComputerEaseAdminDL@deltek.com</a>.
          </p>
          <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "20px 0" }} />
          <h2 style={{ fontSize: 18, fontWeight: 700, marginTop: 0, marginBottom: 8 }}>One more step</h2>
          <p style={{ color: "#4b5563", fontWeight: 700, marginTop: 0, marginBottom: 16, fontSize: 14 }}>
            Please complete the New Customer Form below. This form must be completed before a
            trainer can be assigned to your account. If the form does not load, you can{" "}
            <a href="https://app.smartsheet.com/b/form/559ed37a0cc54aa6b8ae33b428133c89" target="_blank" rel="noopener noreferrer">
              open it in a new tab
            </a>
            .
          </p>
          <iframe
            title="New Customer Form"
            src="https://app.smartsheet.com/b/form/559ed37a0cc54aa6b8ae33b428133c89"
            loading="lazy"
            style={{ width: "100%", height: 1200, border: "1px solid #e5e7eb", borderRadius: 6 }}
          />
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap}>
      <div style={cardWrap}>
        <div style={{ marginBottom: 0, paddingTop: 12, textAlign: "center" }}>
          <img src={logo} alt="Deltek ComputerEase" style={{ height: 28 }} />
        </div>
        <hr style={{ border: "none", borderTop: "1px solid #e5e7eb", margin: "16px 0" }} />
        <h1 style={{ fontSize: 22, marginTop: 0, marginBottom: 12, textAlign: "center" }}>Set Up Your Cloud Users</h1>
        <p style={{ color: "#374151", marginTop: 0, marginBottom: 6, fontWeight: 600, fontSize: 14 }}>
          {context.client_name}
        </p>
        <p style={{ color: "#4b5563", marginTop: 0, marginBottom: 16, fontSize: 14 }}>
          Please provide one user for each licensed cloud seat. Each user must have a first name, last
          name, email address, and a role (Admin or Standard).
          {fixedSeats ? ` You have ${context.seat_count} licensed seat(s).` : ""}
          {" "}This form is for setting up CLOUD user permissions, not ComputerEase (CE)
          application user permissions.
          {" "}If the number of licensed seats displayed does not match the number of users you
          expected, please contact{" "}
          <a href="mailto:ComputerEaseSales@deltek.com">ComputerEaseSales@deltek.com</a> before
          proceeding.
        </p>
        <p style={{ color: "#4b5563", marginTop: 0, marginBottom: 16, fontSize: 14 }}>
          Stuck or have a question about filling out this form? Contact{" "}
          <a href="mailto:ComputerEaseAdminDL@deltek.com">ComputerEaseAdminDL@deltek.com</a> for
          help.
        </p>
        <p style={{ color: "#4b5563", marginTop: 0, marginBottom: 16, fontSize: 14 }}>
          Login credentials will be sent to each user within 2 business days after this form is
          completed.
        </p>
        <div
          style={{
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            borderRadius: 6,
            padding: "10px 12px",
            marginBottom: 0,
          }}
        >
          <p style={{ color: "#4b5563", margin: 0, fontSize: 12 }}>
            Choose Admin or Standard for each user. At least one user must be set up as an Admin
            user. Permissions set now can be updated at a later time if needed.
          </p>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, margin: "10px 0 8px" }}>
          <span style={{ color: "#4b5563", fontSize: 13, marginRight: 4 }}>
            Do you have a long list of users? Download the template and Upload the user details.
          </span>
          <button
            type="button"
            title="Download template"
            onClick={downloadTemplate}
            style={iconBtn}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 4v11" />
            </svg>
          </button>
          <button type="button" title="Import from file" style={iconBtn} onClick={() => fileInputRef.current.click()}>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M17 8l-5-5-5 5M12 3v11" />
            </svg>
          </button>
          <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleImport} style={{ display: "none" }} />
        </div>
        {importError && (
          <p style={{ color: "#b91c1c", fontSize: 14 }}>{importError}</p>
        )}

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", tableLayout: "fixed", borderCollapse: "collapse", fontSize: 14 }}>
            <colgroup>
              <col style={{ width: 36 }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "22%" }} />
              <col />
              <col style={{ width: 120 }} />
              {!fixedSeats && <col style={{ width: 40 }} />}
            </colgroup>
            <thead>
              <tr>
                <th style={th}>#</th>
                <th style={th}>First Name</th>
                <th style={th}>Last Name</th>
                <th style={th}>Email</th>
                <th style={th}>Role</th>
                {!fixedSeats && <th style={th}></th>}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, color: "#9ca3af" }}>{i + 1}</td>
                  <td style={td}>
                    <input
                      style={input}
                      value={r.first_name}
                      onChange={(e) => updateRow(i, "first_name", e.target.value)}
                    />
                  </td>
                  <td style={td}>
                    <input
                      style={input}
                      value={r.last_name}
                      onChange={(e) => updateRow(i, "last_name", e.target.value)}
                    />
                  </td>
                  <td style={td}>
                    <input
                      style={input}
                      type="email"
                      value={r.email}
                      onChange={(e) => updateRow(i, "email", e.target.value)}
                    />
                  </td>
                  <td style={td}>
                    <select
                      style={{ ...input, appearance: "auto", WebkitAppearance: "auto" }}
                      value={r.user_type}
                      onChange={(e) => updateRow(i, "user_type", e.target.value)}
                    >
                      <option value="standard">Standard</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  {!fixedSeats && (
                    <td style={td}>
                      <button
                        type="button"
                        title="Remove"
                        onClick={() => removeRow(i)}
                        disabled={rows.length <= 1}
                        style={{ ...iconBtn, padding: "4px 10px", color: "#dc2626" }}
                      >
                        ✕
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!fixedSeats && (
          <button type="button" style={{ ...iconBtn, marginTop: 12, padding: "6px 14px", fontSize: 13, lineHeight: "normal" }} onClick={addRow}>
            + Add user
          </button>
        )}

        {submitResult && !submitResult.ok && (
          <p style={{ color: "#b91c1c", marginTop: 16 }}>{submitResult.message}</p>
        )}

        {!allRowsValid && (
          <p style={{ color: "#6b7280", fontSize: 14, marginTop: 16, marginBottom: 0, textAlign: "center" }}>
            Fill in every row with a valid name, email, and role to submit.
            {!hasAdmin ? " At least one user must have the Admin role." : ""}
          </p>
        )}

        <div style={{ marginTop: 16, display: "flex", justifyContent: "center" }}>
          <button
            type="button"
            style={{ ...primaryBtn, opacity: allRowsValid && !submitting ? 1 : 0.5 }}
            onClick={submit}
            disabled={!allRowsValid || submitting}
          >
            {submitting ? "Submitting…" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
}
